<script setup lang="ts">
import { computed, inject } from 'vue';
import CopyIcon from '@/assets/icons/shared/copy.svg';
import {
  SECTION_TABLE_NOTIFY_COPIED_KEY,
  SECTION_TABLE_SEARCH_QUERY_KEY,
} from '@/modules/core/consts/section-table-search.const.ts';
import { splitSectionTableSearchHighlight } from '@/modules/core/utils/section-table-search-highlight.util.ts';
import UiHoverPlate from '@/modules/ui/components/ui/UiHoverPlate.vue';

const { text } = defineProps<{
  text: string;
}>();

const searchQuery = inject(SECTION_TABLE_SEARCH_QUERY_KEY, null);
const notifyCopied = inject(SECTION_TABLE_NOTIFY_COPIED_KEY, null);
const parts = computed(() => splitSectionTableSearchHighlight(text, searchQuery?.value?.trim() ?? ''));

async function copy() {
  const value = text?.trim();

  if (!value) {
    return;
  }

  try {
    await navigator.clipboard.writeText(value);
  } catch {
    return;
  }

  notifyCopied?.();
}
</script>

<template>
  <UiHoverPlate
    class="section-table-copy"
    @click.stop="copy"
  >
    <span class="section-table-copy__text">
      <template
        v-for="(part, index) in parts"
        :key="index"
      >
        <b
          v-if="part.match"
          class="section-table-copy__match"
          >{{ part.text }}</b
        >
        <template v-else>{{ part.text }}</template>
      </template>
    </span>

    <template #trailing>
      <CopyIcon
        class="ui-hover-plate__action"
        aria-hidden="true"
      />
    </template>
  </UiHoverPlate>
</template>

<style scoped lang="scss">
.section-table-copy {
  min-width: 0;
  max-width: 100%;
}

.section-table-copy__text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section-table-copy__match {
  font-weight: var(--font-weight-extra-bold);
}
</style>
